import React from "react";
import {
    Menu,
    MenuItem,
    OverlayArrow,
    Popover,
    MenuTrigger as Trigger,
} from "react-aria-components";
import { t } from "ttag";

import { slugify } from "@thelabnyc/thelabui/src/utils/text";

import { datalayerPush } from "../../utils/analytics";
import { ButtonTextSecondary } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./Share.module.scss";

const ShareItem = ({
    children,
    icon,
    href,
    onAction,
}: React.PropsWithChildren<{
    icon: React.ComponentProps<typeof Svg>["name"];
    href?: string;
    onAction?: () => void;
}>) => (
    <MenuItem
        className={styles.shareItem}
        href={href}
        target={href !== undefined ? "_blank" : undefined}
        rel={href !== undefined ? "noopener noreferrer" : undefined}
        onAction={() => {
            const payload = {
                event: "share_interaction",
                share_interaction: "shared",
                share_to:
                    typeof children === "string"
                        ? slugify(children)
                        : undefined,
            };
            datalayerPush(payload);
            if (onAction) onAction();
        }}
    >
        <Svg name={icon} aria-hidden />
        {children}
    </MenuItem>
);

export const ShareMenuPopup = ({ url }: { url: string }) => {
    const encoded = encodeURIComponent(url);
    return (
        <Menu className={styles.popup}>
            <ShareItem
                icon="share/url"
                onAction={() => void navigator.clipboard.writeText(url)}
            >
                {t`Copy Link`}
            </ShareItem>
            <ShareItem icon="share/email" href={`mailto:?body=${encoded}`}>
                {t`Email`}
            </ShareItem>
            <ShareItem
                icon="share/whatsapp"
                href={`https://wa.me/?text=${encoded}`}
            >
                {t`WhatsApp`}
            </ShareItem>
            <ShareItem
                icon="share/facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
            >
                {t`Facebook`}
            </ShareItem>
            <ShareItem
                icon="share/linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`}
            >
                {t`LinkedIn`}
            </ShareItem>
            <ShareItem
                icon="share/twitter"
                href={`https://twitter.com/intent/tweet?url=${encoded}`}
            >
                {t`X`}
            </ShareItem>
            <ShareItem
                icon="share/pinterest"
                href={`https://pinterest.com/pin/create/button/?url=${encoded}`}
            >
                {t`Pinterest`}
            </ShareItem>
        </Menu>
    );
};

export const Share = ({
    url,
    isNonModal,
    offset,
}: {
    url: string;
    /**
     * Do not use outside of storybook!!!
     */
    isNonModal?: boolean;
    offset?: number;
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            const payload = { url };
            const isTouchPrimary =
                window.matchMedia("(pointer: coarse)").matches;
            if (
                isTouchPrimary &&
                typeof navigator.canShare === "function" &&
                navigator.canShare(payload)
            ) {
                datalayerPush({
                    event: "share_interaction",
                    share_interaction: "clicked",
                    shared_url: url,
                });
                navigator.share(payload).catch((err: unknown) => {
                    // User cancelling the native share sheet rejects with
                    // AbortError — that's expected, not an error.
                    if (err instanceof Error && err.name === "AbortError") {
                        return;
                    }
                    console.error("navigator.share failed:", err);
                    // Fall back to the popover so the user still has a way to share.
                    setIsOpen(true);
                });
                return;
            }
        }
        setIsOpen(nextOpen);
        if (nextOpen) {
            datalayerPush({
                event: "share_interaction",
                share_interaction: "clicked",
                shared_url: url,
            });
        }
    };

    return (
        <Trigger isOpen={isOpen} onOpenChange={handleOpenChange}>
            <div className={styles.root}>
                <ButtonTextSecondary>
                    {t`Share`} <Svg name="share" />
                </ButtonTextSecondary>
            </div>
            <Popover placement="bottom" isNonModal={isNonModal} offset={offset}>
                <OverlayArrow className={styles.arrow}>
                    <svg viewBox="0 0 100 75">
                        <polygon points="0,75 100,75 50,0" />
                    </svg>
                </OverlayArrow>
                <ShareMenuPopup url={url} />
            </Popover>
        </Trigger>
    );
};
